import { apiClient } from '@/api/client';
import type { ProductFormValues } from '@/schemas';

export interface Product extends ProductFormValues {
  id: number;
  isActive?: boolean;
  isBlockedFromPeers?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /** Stored discount percentage — set explicitly on create/update; 0 if not provided. */
  discountPct?: number;
}

export interface ProductPage {
  content: Product[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/** Scopes a product text search to a single field instead of the default name-or-sku search. */
export type ProductSearchField = 'code' | 'name' | 'brand';

export interface ProductFilters {
  search?: string;
  searchField?: ProductSearchField;
  brands?: string[];
  schemeOnly?: boolean;
  stockFilter?: 'IN_STOCK' | 'LOW_STOCK' | 'NO_STOCK';
  /** Required when stockFilter is 'LOW_STOCK' — the tenant's configured threshold. */
  lowStockThreshold?: number;
  holdOnly?: boolean;
  statusFilter?: 'ACTIVE' | 'INACTIVE';
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/** GET /products — paginated + filtered list scoped to the calling tenant. */
export async function listProducts(
  page = 0,
  size = 25,
  filters?: ProductFilters,
): Promise<ProductPage> {
  // Build params manually using URLSearchParams so repeated brand values are
  // serialized as brand=A&brand=B (what Spring @RequestParam List<String> expects),
  // not brand[]=A&brand[]=B which is axios's default for arrays.
  const p = new URLSearchParams();
  p.append('page', String(page));
  p.append('size', String(size));
  if (filters?.search?.trim()) {
    p.append('search', filters.search.trim());
    if (filters.searchField) p.append('searchField', filters.searchField);
  }
  filters?.brands?.forEach(b => p.append('brand', b));
  if (filters?.schemeOnly) p.append('schemeOnly', 'true');
  if (filters?.stockFilter) p.append('stockFilter', filters.stockFilter);
  if (filters?.stockFilter === 'LOW_STOCK' && filters?.lowStockThreshold != null) {
    p.append('lowStockThreshold', String(filters.lowStockThreshold));
  }
  if (filters?.holdOnly) p.append('holdOnly', 'true');
  if (filters?.statusFilter) p.append('statusFilter', filters.statusFilter);
  if (filters?.sortBy) p.append('sortBy', filters.sortBy);
  if (filters?.sortDir) p.append('sortDir', filters.sortDir);

  const { data } = await apiClient.get<ProductPage>(`/products?${p.toString()}`);
  return data;
}

/** GET /products/all — all non-deleted products for client-side filtering. */
export async function listAllProducts(): Promise<Product[]> {
  const { data } = await apiClient.get<Product[]>('/products/all');
  return data;
}

/** GET /products/brands — distinct brand names for the filter panel. */
export async function getProductBrands(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>('/products/brands');
  return data;
}

/** GET /products/{id} — fetch a single product, used by the edit form. */
export async function getProduct(id: number): Promise<Product> {
  const { data } = await apiClient.get<Product>(`/products/${id}`);
  return data;
}

/** POST /products — create one product. */
export async function createProduct(payload: ProductFormValues): Promise<Product> {
  const { data } = await apiClient.post<Product>('/products', payload);
  return data;
}

/** PUT /products/{id} — edit one product. */
export async function updateProduct(
  id: number,
  payload: ProductFormValues,
): Promise<Product> {
  const { data } = await apiClient.put<Product>(`/products/${id}`, payload);
  return data;
}

/** PATCH /products/{id}/active — toggle isActive. */
export async function toggleProductActive(id: number): Promise<Product> {
  const { data } = await apiClient.patch<Product>(`/products/${id}/active`);
  return data;
}

/** PATCH /products/{id}/qty — update available quantity. */
export async function updateProductQty(id: number, qty: number): Promise<Product> {
  const { data } = await apiClient.patch<Product>(`/products/${id}/qty`, { qty });
  return data;
}

/** PATCH /products/{id}/peer-visibility — toggle isBlockedFromPeers. */
export async function toggleProductPeerVisibility(id: number): Promise<Product> {
  const { data } = await apiClient.patch<Product>(`/products/${id}/peer-visibility`);
  return data;
}

/**
 * A product from a peer (distributor) tenant's catalog.
 * Returned by GET /products/cross-tenant.
 */
export interface CrossTenantProduct {
  productId: number;
  sku: string;
  name: string;
  brand?: string;
  genericName?: string;
  packSize?: string;
  uom: string;
  gstRate?: number;
  mrp?: number;
  ptr?: number;
  pts?: number;
  /** Server-computed (MRP − PTR) / MRP × 100, rounded to 2 decimals. Null when priceHidden or MRP/PTR unavailable. */
  discountPct?: number;
  availableQty?: number;
  scheme?: string;
  minOrderQty?: number;
  /** Near-expiry month in "mm-yyyy" format, e.g. "08-2026". */
  nearExpiry?: string;
  distributorTenantId: number;
  distributorName: string;
  distributorCity?: string;
  distributorArea?: string;
  priorityRank: number;
  stockHidden?: boolean;
  priceHidden?: boolean;
  approxRate?: number;
  schemeHidden?: boolean;
  minOrderAmount?: number;
}

/**
 * GET /products/cross-tenant — search products across multiple
 * distributor tenants in priority order.
 *
 * @param distributorTenantIds ordered list (priority 0 = first)
 * @param q search term matched against name / SKU
 */
export async function searchCrossTenantProducts(
  distributorTenantIds: number[],
  q: string,
  retailerTenantId?: number,
): Promise<CrossTenantProduct[]> {
  if (distributorTenantIds.length === 0) return [];
  const p = new URLSearchParams();
  distributorTenantIds.forEach(id => p.append('distributorTenantIds', String(id)));
  if (q.trim()) p.append('q', q.trim());
  if (retailerTenantId) p.append('retailerTenantId', String(retailerTenantId));
  const { data } = await apiClient.get<CrossTenantProduct[]>(
    `/products/cross-tenant?${p.toString()}`,
  );
  return data;
}

export type ProductListParams = ProductFilters & { page?: number; size?: number };

export const productsApi = {
  list: (params: ProductListParams = {}) =>
    listProducts(params.page ?? 0, params.size ?? 25, params),
  get: getProduct,
  create: (product: unknown) => createProduct(product as ProductFormValues),
  update: (id: number, updates: unknown) => updateProduct(id, updates as ProductFormValues),
  delete: (id: number): Promise<void> =>
    apiClient.delete(`/products/${id}`).then(() => undefined),
  batches: (productId: number): Promise<unknown[]> =>
    apiClient.get<unknown[]>(`/products/${productId}/batches`).then(r => r.data),
  stock: (productId: number): Promise<unknown> =>
    apiClient.get<unknown>(`/products/${productId}/stock`).then(r => r.data),
};

export interface BulkResult {
  receivedCount: number;
  createdCount: number;
  failedCount: number;
  created: Product[];
  failures: Array<{ index: number; sku?: string; code: string; message: string }>;
}

/** POST /products/bulk — create up to 1000 products in one call. */
export async function createProductsBulk(
  products: ProductFormValues[],
): Promise<BulkResult> {
  const { data } = await apiClient.post<BulkResult>('/products/bulk', { products });
  return data;
}
